<template>
  <div class="flex flex-col gap-2 group">
    <!-- <div class="leading-3 text-xs font-medium tracking-widest hidden lg:block">DESIGN</div> -->
    <label
      :for="id"
      class="whitespace-nowrap flex gap-2 lg:gap-2 justify-center cursor-pointer items-center flex-grow px-4 bg-white text-text-content border rounded-lg h-10 font-medium"
      :class="[btnClass]"
    >
      <div
        v-if="designUploaded"
        class="rounded flex-shrink-0 p-1 bg-black bg-opacity-5 border border-border-primary"
      >
        <img
          class="w-4 h-4 rounded preview-img object-cover"
          :src="design"
          alt=""
        />
      </div>
      <div class="order-2">
        <Icon icon="solar:upload-minimalistic-linear" width="16" height="16" />
      </div>
      <!-- <div v-if="designUploaded" class="border h-6 opacity-20 btn-divider"></div> -->
      <transition name="fade" mode="out-in">
        <span
          :key="designUploaded + '-' + id"
          class="text-label-lg text-left truncate max-w-28 transition-all duration-300 ease-in-out"
          :class="{
            'group-hover:max-w-0': designUploaded && id === 'ai-preview',
          }"
        >
          {{ designUploaded ? fileName : $t(label, label) }}
        </span>
      </transition>
    </label>
    <input
      :id="id"
      placeholder="Upload"
      class="mt-2 hidden"
      type="file"
      accept="image/*"
      @change="handleFileUpload"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import { Icon } from "@iconify/vue";
import {
  getPaddedImageUrl,
  getImageUrlFromLocalStorage,
} from "../../util/common.util";

const FILE_NAME_LENGTH = 10;

const props = defineProps({
  label: {
    type: String,
    default: "upload-design",
  },
  icon: {
    type: String,
  },
  btnClass: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: "imgPicker",
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
  clearDesign: {
    type: Boolean,
    default: false,
  },
  defaultImage: {
    type: String,
  },
  storageKey: {
    type: String,
  },
  isDesign: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["upload"]);

const designUploaded = ref(false);
const design = ref(null);
const fileName = ref(null);

const route = useRoute();

watch(
  () => props.defaultImage,
  (newValue) => {
    if (newValue) {
      design.value = newValue;
      designUploaded.value = true;
    }
  },
);

onMounted(async () => {
  if (props.defaultImage) {
    design.value = props.defaultImage;
    designUploaded.value = true;
  } else if (props.storageKey) {
    const persist = route?.query?.persist;
    const designUrl = await getImageUrlFromLocalStorage(
      props.storageKey,
      "",
      persist ? true : false,
    );
    if (designUrl && !props.clearDesign) {
      design.value = designUrl;
      designUploaded.value = true;
    }
  }
  // Retrieve stored filename if exists
  if (localStorage.getItem("fileName")) {
    fileName.value = localStorage.getItem("fileName");
  }
});

function getShortFileName(name) {
  if (name.length <= FILE_NAME_LENGTH) {
    return name;
  }
  return `${name.substring(0, FILE_NAME_LENGTH)}...`;
}

async function handleFileUpload(e) {
  const originalName = e.target.files[0].name;
  fileName.value = originalName;
  localStorage.setItem("fileName", originalName);

  fileName.value = getShortFileName(originalName);

  if (props.isDesign) {
    const data = await getPaddedImageUrl(e.target.files[0]);
    const fileUrl = data.url;
    const base64File = data.base64;

    try {
      if (props.storageKey) {
        localStorage.setItem(props.storageKey, fileUrl);
        if (props.storageKey === "design-file") {
          localStorage.setItem(`${props.storageKey}-base64`, base64File);
        }
      }
    } catch (error) {
      localStorage.removeItem(`${props.storageKey}-base64`);
      console.error("Error saving in local storage:", error);
    }

    design.value = fileUrl;
    emit("upload", fileUrl);
  } else {
    const imageUrl = URL.createObjectURL(e.target.files[0]);
    design.value = imageUrl;
    emit("upload", imageUrl);
  }
  designUploaded.value = true;
  e.target.value = "";
}
</script>

<style scoped>
.preview-img {
  max-height: 38px;
}

.btn-divider {
  margin-left: 10px;
  margin-right: 10px;
}
</style>
